/**
 * /sondage — Bilingual field-research poll (FR + EN)
 *
 * Typeform-style: one question per screen, progress bar, smooth transitions.
 * State machine drives 10 questions (6 if Q1=none, skipping laundress questions).
 * Saves to D1 via Pages Function at /v1/marketing/survey.
 * Emails founder on each submission. WhatsApp re-share on thank-you screen.
 *
 * Locale: accepts a `locale` prop ('fr' | 'en'), defaults to 'fr'.
 * The optional `onLocaleChange` prop allows the survey to toggle locale (FR/EN button).
 * All strings go through translate(key, locale) — zero hardcoded copy.
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';
import { translate } from '@washed/i18n';
import type { WashedLocale } from '@washed/i18n';
import { LOME_QUARTIERS_SORTED } from '../data/lome-quartiers.js';
import { submitSurvey } from '../api/survey.js';
import type { SurveyPayload } from '../api/survey.js';
import { TurnstileWidget, TURNSTILE_ENABLED } from '../components/TurnstileWidget.js';
import styles from './SurveyPage.module.css';

// ── Step types ──────────────────────────────────────────────────────────────

type Q1Answer = 'regular' | 'sometimes' | 'none';
type Q2Answer = 'weekly' | 'biweekly' | 'monthly' | 'on_demand';
type Q3Answer = 'under_2k' | '2_3k' | '3_5k' | '5_10k' | 'over_10k' | 'varies';
type Q4Answer = 'alone' | 'whole_household' | 'partial';
type Q5Answer = 'unreliable' | 'disappears' | 'quality' | 'price_changes' | 'lost_damaged' | 'none';
type Q6Answer = '1' | '2_3' | '4_5' | '6_plus';
type Q7Q8Answer = 'yes_definitely' | 'yes_maybe' | 'unsure' | 'no';

type Step =
  'q1' | 'q2' | 'q3' | 'q4' | 'q5' | 'q6' | 'q7' | 'q8' | 'q9' | 'q10' | 'submitting' | 'done';

interface ContactFields {
  q10: string;
  q10_email: string;
  consentTerms: boolean;
}

interface SurveyState {
  step: Step;
  animating: boolean;
  q1?: Q1Answer;
  q2?: Q2Answer;
  q3?: Q3Answer;
  q4?: Q4Answer;
  q5: Q5Answer[];
  q6?: Q6Answer;
  q7?: Q7Q8Answer;
  q8?: Q7Q8Answer;
  q9: string;
  q10: string;
  q10_email: string;
  error?: string;
}

type Action =
  | { type: 'NEXT_STEP'; next: Step }
  | { type: 'DONE_ANIMATING' }
  | { type: 'SET_Q1'; value: Q1Answer }
  | { type: 'SET_Q2'; value: Q2Answer }
  | { type: 'SET_Q3'; value: Q3Answer }
  | { type: 'SET_Q4'; value: Q4Answer }
  | { type: 'TOGGLE_Q5'; value: Q5Answer }
  | { type: 'SET_Q6'; value: Q6Answer }
  | { type: 'SET_Q7'; value: Q7Q8Answer }
  | { type: 'SET_Q8'; value: Q7Q8Answer }
  | { type: 'SET_Q9'; value: string }
  | { type: 'SET_Q10'; value: string }
  | { type: 'SET_Q10_EMAIL'; value: string }
  | { type: 'SET_CONTACT'; value: ContactFields }
  | { type: 'BACK' }
  | { type: 'SUBMIT_DONE' }
  | { type: 'SUBMIT_ERROR'; message: string };

const INITIAL_STATE: SurveyState = {
  step: 'q1',
  animating: false,
  q5: [],
  q9: '',
  q10: '',
  q10_email: '',
};

// Step order depends on q1 branching
function nextStepFor(current: Step, state: SurveyState): Step {
  const hasLaundress = state.q1 !== 'none';
  const order: Step[] = hasLaundress
    ? ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10', 'submitting', 'done']
    : ['q1', 'q6', 'q7', 'q8', 'q9', 'q10', 'submitting', 'done'];
  const idx = order.indexOf(current);
  if (idx >= 0 && idx < order.length - 1) {
    return order[idx + 1] as Step;
  }
  return 'done';
}

function prevStepFor(current: Step, state: SurveyState): Step {
  const hasLaundress = state.q1 !== 'none';
  const order: Step[] = hasLaundress
    ? ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10', 'submitting', 'done']
    : ['q1', 'q6', 'q7', 'q8', 'q9', 'q10', 'submitting', 'done'];
  const idx = order.indexOf(current);
  if (idx > 0) {
    return order[idx - 1] as Step;
  }
  return 'q1';
}

function totalVisibleQuestions(q1?: Q1Answer): number {
  return q1 !== 'none' ? 10 : 6;
}

function currentQuestionNumber(step: Step, q1?: Q1Answer): number {
  const hasLaundress = q1 !== 'none';
  const order: Step[] = hasLaundress
    ? ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10']
    : ['q1', 'q6', 'q7', 'q8', 'q9', 'q10'];
  const idx = order.indexOf(step);
  return idx >= 0 ? idx + 1 : 0;
}

function reducer(state: SurveyState, action: Action): SurveyState {
  switch (action.type) {
    case 'NEXT_STEP':
      return { ...state, step: action.next, animating: true };
    case 'DONE_ANIMATING':
      return { ...state, animating: false };
    case 'SET_Q1':
      return { ...state, q1: action.value };
    case 'SET_Q2':
      return { ...state, q2: action.value };
    case 'SET_Q3':
      return { ...state, q3: action.value };
    case 'SET_Q4':
      return { ...state, q4: action.value };
    case 'TOGGLE_Q5': {
      const already = state.q5.includes(action.value);
      return {
        ...state,
        q5: already ? state.q5.filter((v) => v !== action.value) : [...state.q5, action.value],
      };
    }
    case 'SET_Q6':
      return { ...state, q6: action.value };
    case 'SET_Q7':
      return { ...state, q7: action.value };
    case 'SET_Q8':
      return { ...state, q8: action.value };
    case 'SET_Q9':
      return { ...state, q9: action.value };
    case 'SET_Q10':
      return { ...state, q10: action.value };
    case 'SET_Q10_EMAIL':
      return { ...state, q10_email: action.value };
    case 'SET_CONTACT':
      return { ...state, q10: action.value.q10, q10_email: action.value.q10_email };
    case 'BACK': {
      const prev = prevStepFor(state.step, state);
      return { ...state, step: prev, animating: true };
    }
    case 'SUBMIT_DONE':
      return { ...state, step: 'done', animating: false };
    case 'SUBMIT_ERROR':
      return { ...state, step: 'q10', error: action.message, animating: false };
    default:
      return state;
  }
}

// ── Main component ───────────────────────────────────────────────────────────

interface SurveyPageProps {
  locale?: WashedLocale;
  onLocaleChange?: (locale: WashedLocale) => void;
}

export function SurveyPage({ locale = 'fr', onLocaleChange }: SurveyPageProps = {}) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const t = (key: Parameters<typeof translate>[0]) => translate(key, locale);

  // Set page title + meta description on mount / locale change
  useEffect(() => {
    document.title = t('survey.page.title');
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', t('survey.page.meta'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // After animating in, mark done
  useEffect(() => {
    if (!state.animating) return;
    const timer = setTimeout(() => dispatch({ type: 'DONE_ANIMATING' }), 260);
    return () => clearTimeout(timer);
  }, [state.animating, state.step]);

  // H-2: Turnstile token held in a ref (set by the widget callback, read at submit).
  const turnstileTokenRef = useRef('');
  const handleTurnstile = useCallback((token: string) => {
    turnstileTokenRef.current = token;
  }, []);

  const advance = (next?: Step, contact?: ContactFields) => {
    const target = next ?? nextStepFor(state.step, state);
    if (target === 'submitting') {
      // H-2: require a solved challenge when Turnstile is configured.
      if (TURNSTILE_ENABLED && !turnstileTokenRef.current) {
        dispatch({
          type: 'SUBMIT_ERROR',
          message: t('survey.turnstile.error'),
        });
        return;
      }
      if (contact) {
        dispatch({ type: 'SET_CONTACT', value: contact });
      }
      doSubmit(contact);
    } else {
      dispatch({ type: 'NEXT_STEP', next: target });
    }
  };

  const doSubmit = async (contact?: ContactFields) => {
    dispatch({ type: 'NEXT_STEP', next: 'submitting' });
    const q10 = contact?.q10 ?? state.q10;
    const q10Email = contact?.q10_email ?? state.q10_email;
    const consentTerms = contact?.consentTerms ?? false;
    const payload: SurveyPayload = {
      q1_has_laundress: state.q1!,
      q2_frequency: state.q2 ?? null,
      q3_monthly_pay: state.q3 ?? null,
      q4_for_whom: state.q4 ?? null,
      q5_frustrations: state.q5.length > 0 ? state.q5 : null,
      q6_household_size: state.q6!,
      q7_pay_2500: state.q7!,
      q8_pay_4500: state.q8!,
      q9_quartier: state.q9.trim() || null,
      q10_phone: q10.trim() || null,
      q10_email: q10Email.trim().toLowerCase() || null,
      consentTerms,
      cf_turnstile_response: turnstileTokenRef.current || undefined,
      locale,
    };
    try {
      await submitSurvey(payload);
      dispatch({ type: 'SUBMIT_DONE' });
    } catch (e) {
      dispatch({ type: 'SUBMIT_ERROR', message: submissionErrorMessage(e, locale) });
    }
  };

  const isQStep = !['submitting', 'done'].includes(state.step);
  const total = totalVisibleQuestions(state.q1);
  const current = currentQuestionNumber(state.step, state.q1);
  const progress = isQStep ? current / total : 1;

  if (state.step === 'submitting') {
    return (
      <main>
        <div className={styles.shell}>
          <div className={styles.centered}>
            <p className={styles.submitting}>{t('survey.submitting')}</p>
          </div>
        </div>
      </main>
    );
  }

  if (state.step === 'done') {
    const shareMsg = encodeURIComponent(t('survey.thanks.share_message'));
    return (
      <main>
        <div className={styles.shell}>
          <div className={`${styles.screen} ${styles.thankYou}`}>
            <h1 className={styles.thanksHeading}>{t('survey.thanks.title')}</h1>
            <p className={styles.thanksSub}>{t('survey.thanks.body')}</p>
            <a
              href={`https://wa.me/?text=${shareMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.whatsappBtn}
            >
              <WhatsAppIcon /> {t('survey.thanks.share_button')}
            </a>
            <a href="/" className={styles.homeLink}>
              {t('survey.thanks.home_link')}
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className={styles.shell}>
        {/* Visually-hidden h1 — required landmark heading; screen-readers announce page identity */}
        <h1 className="sr-only">{locale === 'fr' ? 'Sondage Washed' : 'Washed survey'}</h1>

        {/* Progress bar */}
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-label={t('survey.progress.aria')}
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={total}
        >
          <div className={styles.progressFill} style={{ width: `${progress * 100}%` }} />
        </div>

        {/* FR / EN toggle */}
        {onLocaleChange && (
          <div className={styles.langToggle}>
            <button
              className={`${styles.langBtn} ${locale === 'fr' ? styles.langActive : ''}`}
              onClick={() => onLocaleChange('fr')}
              aria-pressed={locale === 'fr'}
            >
              FR
            </button>
            <button
              className={`${styles.langBtn} ${locale === 'en' ? styles.langActive : ''}`}
              onClick={() => onLocaleChange('en')}
              aria-pressed={locale === 'en'}
            >
              EN
            </button>
          </div>
        )}

        <div className={`${styles.screen} ${state.animating ? styles.fadeOut : styles.fadeIn}`}>
          {/* Eyebrow */}
          {current > 0 && (
            <div className={styles.eyebrow}>
              {t('survey.eyebrow')
                .replace('{current}', String(current))
                .replace('{total}', String(total))}
            </div>
          )}

          {/* Back button */}
          {state.step !== 'q1' && (
            <button
              className={styles.backBtn}
              onClick={() => dispatch({ type: 'BACK' })}
              aria-label={t('survey.nav.back.aria')}
            >
              {t('survey.nav.back')}
            </button>
          )}

          {/* Question content */}
          <QuestionScreen
            state={state}
            dispatch={dispatch}
            advance={advance}
            onTurnstileToken={handleTurnstile}
            locale={locale}
          />
        </div>
      </div>
    </main>
  );
}

// ── Question screens ─────────────────────────────────────────────────────────

interface QProps {
  state: SurveyState;
  dispatch: React.Dispatch<Action>;
  advance: (next?: Step, contact?: ContactFields) => void;
  onTurnstileToken: (token: string) => void;
  locale: WashedLocale;
}

function QuestionScreen({ state, dispatch, advance, onTurnstileToken, locale }: QProps) {
  const t = (key: Parameters<typeof translate>[0]) => translate(key, locale);

  switch (state.step) {
    case 'q1':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q1.text')}</h2>
          <div className={styles.options}>
            {(
              [
                ['regular', t('survey.q1.option.regular')],
                ['sometimes', t('survey.q1.option.sometimes')],
                ['none', t('survey.q1.option.none')],
              ] as [Q1Answer, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                className={`${styles.option} ${state.q1 === val ? styles.selected : ''}`}
                onClick={() => {
                  dispatch({ type: 'SET_Q1', value: val });
                  advance(val === 'none' ? 'q6' : 'q2');
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );

    case 'q2':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q2.text')}</h2>
          <div className={styles.options}>
            {(
              [
                ['weekly', t('survey.q2.option.weekly')],
                ['biweekly', t('survey.q2.option.biweekly')],
                ['monthly', t('survey.q2.option.monthly')],
                ['on_demand', t('survey.q2.option.on_demand')],
              ] as [Q2Answer, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                className={`${styles.option} ${state.q2 === val ? styles.selected : ''}`}
                onClick={() => {
                  dispatch({ type: 'SET_Q2', value: val });
                  advance();
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );

    case 'q3':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q3.text')}</h2>
          <div className={styles.options}>
            {(
              [
                ['under_2k', t('survey.q3.option.under_2k')],
                ['2_3k', t('survey.q3.option.2_3k')],
                ['3_5k', t('survey.q3.option.3_5k')],
                ['5_10k', t('survey.q3.option.5_10k')],
                ['over_10k', t('survey.q3.option.over_10k')],
                ['varies', t('survey.q3.option.varies')],
              ] as [Q3Answer, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                className={`${styles.option} ${state.q3 === val ? styles.selected : ''}`}
                onClick={() => {
                  dispatch({ type: 'SET_Q3', value: val });
                  advance();
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );

    case 'q4':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q4.text')}</h2>
          <div className={styles.options}>
            {(
              [
                ['alone', t('survey.q4.option.alone')],
                ['whole_household', t('survey.q4.option.whole_household')],
                ['partial', t('survey.q4.option.partial')],
              ] as [Q4Answer, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                className={`${styles.option} ${state.q4 === val ? styles.selected : ''}`}
                onClick={() => {
                  dispatch({ type: 'SET_Q4', value: val });
                  advance();
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );

    case 'q5':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q5.text')}</h2>
          <p className={styles.multiHint}>{t('survey.q5.multi_hint')}</p>
          <div className={styles.options}>
            {(
              [
                ['unreliable', t('survey.q5.option.unreliable')],
                ['disappears', t('survey.q5.option.disappears')],
                ['quality', t('survey.q5.option.quality')],
                ['price_changes', t('survey.q5.option.price_changes')],
                ['lost_damaged', t('survey.q5.option.lost_damaged')],
                ['none', t('survey.q5.option.none')],
              ] as [Q5Answer, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                className={`${styles.option} ${state.q5.includes(val) ? styles.selected : ''}`}
                onClick={() => dispatch({ type: 'TOGGLE_Q5', value: val })}
              >
                {state.q5.includes(val) && <span className={styles.checkMark}>✓ </span>}
                {label}
              </button>
            ))}
          </div>
          <button
            className={styles.confirmBtn}
            onClick={() => advance()}
            disabled={state.q5.length === 0}
          >
            {t('survey.confirm')}
          </button>
        </div>
      );

    case 'q6':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q6.text')}</h2>
          <div className={styles.options}>
            {(
              [
                ['1', t('survey.q6.option.1')],
                ['2_3', t('survey.q6.option.2_3')],
                ['4_5', t('survey.q6.option.4_5')],
                ['6_plus', t('survey.q6.option.6_plus')],
              ] as [Q6Answer, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                className={`${styles.option} ${state.q6 === val ? styles.selected : ''}`}
                onClick={() => {
                  dispatch({ type: 'SET_Q6', value: val });
                  advance();
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );

    case 'q7':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>
            {/* Q7 text has price embedded inline — render from key */}
            {t('survey.q7.text')}
          </h2>
          <WillingnessOptions
            selected={state.q7}
            onSelect={(val) => {
              dispatch({ type: 'SET_Q7', value: val });
              advance();
            }}
            locale={locale}
          />
        </div>
      );

    case 'q8':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>
            {/* Q8 text has price embedded inline — render from key */}
            {t('survey.q8.text')}
          </h2>
          <WillingnessOptions
            selected={state.q8}
            onSelect={(val) => {
              dispatch({ type: 'SET_Q8', value: val });
              advance();
            }}
            locale={locale}
          />
        </div>
      );

    case 'q9':
      return (
        <div className={styles.question}>
          <h2 className={styles.qText}>{t('survey.q9.text')}</h2>
          <p className={styles.optionalHint}>{t('survey.q9.optional_hint')}</p>
          <datalist id="lome-quartiers-survey">
            {LOME_QUARTIERS_SORTED.map((q) => (
              <option key={q} value={q} />
            ))}
          </datalist>
          <input
            type="text"
            list="lome-quartiers-survey"
            className={styles.textInput}
            placeholder={t('survey.q9.placeholder')}
            value={state.q9}
            onChange={(e) => dispatch({ type: 'SET_Q9', value: e.target.value })}
            autoComplete="off"
            aria-label={t('survey.q9.aria')}
          />
          <div className={styles.inputActions}>
            <button
              className={styles.confirmBtn}
              onClick={() => advance()}
              disabled={!state.q9.trim()}
            >
              {t('survey.confirm')}
            </button>
            <button className={styles.skipBtn} onClick={() => advance()}>
              {t('survey.skip')}
            </button>
          </div>
        </div>
      );

    case 'q10':
      return (
        <form
          className={styles.question}
          onSubmit={(e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            const phone = stringFormValue(data.get('q10_phone'));
            const email = stringFormValue(data.get('q10_email'));
            const consentTerms = data.get('consentTerms') === 'on';
            // H-4: consent required only when PII (phone/email) is provided.
            if ((phone || email) && !consentTerms) {
              dispatch({
                type: 'SUBMIT_ERROR',
                message: t('survey.error.consent'),
              });
              return;
            }
            advance(undefined, { q10: phone, q10_email: email, consentTerms });
          }}
        >
          <h2 className={styles.qText}>{t('survey.q10.text')}</h2>
          <p className={styles.optionalHint}>{t('survey.q10.optional_hint')}</p>
          <input
            type="tel"
            name="q10_phone"
            className={styles.textInput}
            placeholder={t('survey.q10.placeholder.phone')}
            value={state.q10}
            onChange={(e) => dispatch({ type: 'SET_Q10', value: e.target.value })}
            autoComplete="tel"
            inputMode="tel"
            aria-label={t('survey.q10.aria.phone')}
          />
          <input
            type="email"
            name="q10_email"
            className={styles.textInput}
            placeholder={t('survey.q10.placeholder.email')}
            value={state.q10_email}
            onChange={(e) => dispatch({ type: 'SET_Q10_EMAIL', value: e.target.value })}
            autoComplete="email"
            inputMode="email"
            aria-label={t('survey.q10.aria.email')}
          />
          {/* H-4: consent gate. Always rendered (robust to browser autofill that
              bypasses React change events); enforced at submit only when PII is present. */}
          <label className={styles.consentLabel}>
            <input type="checkbox" name="consentTerms" aria-label={t('survey.q10.aria.consent')} />
            <span>{t('survey.q10.consent_label')}</span>
          </label>
          {/* H-2: bot challenge (renders only when VITE_TURNSTILE_SITEKEY is set). */}
          <TurnstileWidget onToken={onTurnstileToken} />
          {state.error && <p className={styles.errorMsg}>{state.error}</p>}
          <div className={styles.inputActions}>
            <button type="submit" className={styles.confirmBtn}>
              {state.q10.trim() || state.q10_email.trim()
                ? t('survey.q10.submit.with_contact')
                : t('survey.q10.submit.no_contact')}
            </button>
            <button
              type="button"
              className={styles.skipBtn}
              onClick={() => advance(undefined, { q10: '', q10_email: '', consentTerms: false })}
            >
              {t('survey.skip')}
            </button>
          </div>
        </form>
      );

    default:
      return null;
  }
}

function WillingnessOptions({
  selected,
  onSelect,
  locale,
}: {
  selected?: Q7Q8Answer | undefined;
  onSelect: (val: Q7Q8Answer) => void;
  locale: WashedLocale;
}) {
  const t = (key: Parameters<typeof translate>[0]) => translate(key, locale);
  return (
    <div className={styles.options}>
      {(
        [
          ['yes_definitely', t('survey.q7q8.option.yes_definitely')],
          ['yes_maybe', t('survey.q7q8.option.yes_maybe')],
          ['unsure', t('survey.q7q8.option.unsure')],
          ['no', t('survey.q7q8.option.no')],
        ] as [Q7Q8Answer, string][]
      ).map(([val, label]) => (
        <button
          key={val}
          className={`${styles.option} ${selected === val ? styles.selected : ''}`}
          onClick={() => onSelect(val)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function stringFormValue(value: FormDataEntryValue | null): string {
  return typeof value === 'string' ? value : '';
}

function submissionErrorMessage(error: unknown, locale: WashedLocale): string {
  const field =
    typeof error === 'object' && error !== null && 'field' in error
      ? (error as { field?: unknown }).field
      : undefined;
  if (field === 'q10_phone') return translate('survey.error.phone', locale);
  if (field === 'q10_email') return translate('survey.error.email', locale);
  return translate('survey.error.submit', locale);
}

function WhatsAppIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
